import { useEffect, useState } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { CHECK, EVENT_NAME, Tabs } from 'common/constants';
import { useAmplitude } from 'components/AmplitudeProvider';
import { useFlagValue } from 'hooks/useFlags';
import { PaymentMethod } from 'pages/HighVolumeLandingPage/constants';
import { getSelectedTab } from 'pages/LandingPage/components/LandingPageHeader/slice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { amplitudeTracker } from 'utils/AmplitudeTracker';
import {
  getPaymentMethods,
  getSelectedInvoicesLength,
  setPaymentMethods,
  setFullPageLoader,
  getVendorsResponse
} from '../VendorsTable/slice';
import { PAYMENT_METHOD } from './constants';
import { options } from './options';
import {
  StyledFilterChips,
  StyledPaymentMethodDiv,
  StyledPaymentMethodLabelSpan
} from './styled';

export const PaymentMethodChips = (): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const selectedInvoicesLength = useAppSelector(getSelectedInvoicesLength);
  const selectedPaymentMethods = useAppSelector(getPaymentMethods);
  const vendorsData = useAppSelector(getVendorsResponse);
  const selectedTab = useAppSelector(getSelectedTab);
  const [initialLanding, setInitialLanding] = useState<boolean>(true);
  const landingPageFlag = useFlagValue('dmsAccountingLandingPage');
  const intl: IntlShape = useIntl();
  const amplitude = useAmplitude();

  useEffect(() => {
    if (
      selectedTab === Tabs.NeedsAuthorization &&
      !selectedInvoicesLength &&
      (!selectedPaymentMethods.length || selectedPaymentMethods.length !== 1)
    ) {
      dispatch(
        setPaymentMethods([
          landingPageFlag === true ? PaymentMethod.VIRTUAL_CARD : CHECK
        ])
      );
    }
  }, [
    dispatch,
    selectedInvoicesLength,
    selectedPaymentMethods.length,
    landingPageFlag,
    selectedTab
  ]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (
      landingPageFlag === true &&
      initialLanding &&
      selectedTab === Tabs.NeedsAuthorization &&
      vendorsData &&
      vendorsData?.vendors?.length === 0
    ) {
      timeoutId = setTimeout(() => {
        dispatch(
          setPaymentMethods(({ paymentMethods: prevPaymentMethods }) =>
            prevPaymentMethods[0] === PaymentMethod.VIRTUAL_CARD
              ? [PaymentMethod.AP_CHECK]
              : prevPaymentMethods
          )
        );
        setInitialLanding(false);
      }, 0);
    }
    return () => clearTimeout(timeoutId);
  }, [dispatch, initialLanding, landingPageFlag, selectedTab, vendorsData]);

  useEffect(() => {
    setInitialLanding(true);
  }, [selectedTab]);

  const handlePaymentMethod = (newPaymentMethods: string[]) => {
    if (selectedTab === Tabs.NeedsAuthorization) {
      const paymentMethod = newPaymentMethods.find(
        method => method !== selectedPaymentMethods[0]
      );
      if (paymentMethod) {
        dispatch(setPaymentMethods([paymentMethod]));
      }
    } else {
      dispatch(setPaymentMethods(newPaymentMethods));
      if (newPaymentMethods.length) {
        amplitudeTracker(
          amplitude,
          newPaymentMethods[newPaymentMethods.length - 1] === CHECK
            ? EVENT_NAME.SelectCheckFilter
            : EVENT_NAME.SelectCreditCard
        );
      }
    }
    dispatch(setFullPageLoader(true));
  };

  return (
    <StyledPaymentMethodDiv>
      <StyledPaymentMethodLabelSpan>
        <FormattedMessage
          defaultMessage="Payment Method"
          description="Payment Method Label"
        />
      </StyledPaymentMethodLabelSpan>
      <StyledFilterChips
        dataTestId={`${PAYMENT_METHOD}`}
        onChange={handlePaymentMethod}
        options={options(intl, landingPageFlag)}
        value={selectedPaymentMethods}
        $isNeedsAuthorization={selectedTab === Tabs.NeedsAuthorization}
      />
    </StyledPaymentMethodDiv>
  );
};
